// pages/PriceTagFormPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import priceTagService from '../services/priceTagService';
import productService from '../services/productService';
import storeService from '../services/storeService';
import { getIcon } from '../utils/icons';
import { getWeightUnitDisplayName, getVolumeUnitDisplayName } from '../services/unitsService';

/**
 * Страница формы добавления/редактирования ценника
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const PriceTagFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Заголовок страницы в зависимости от режима
  const pageTitle = isEditMode ? 'Редактирование ценника' : 'Добавление нового ценника';
  
  // Состояние формы
  const [formData, setFormData] = useState({
    productId: '',
    storeId: '',
    price: '',
    pricePerUnit: '',
    unitId: '',
    discountPrice: '',
    discountStartDate: '',
    discountEndDate: '',
    tagType: 'Printed',
    category: 'Minimum',
    currency: 'RUB',
    imageId: '',
    weightAmount: '',
    weightUnit: '',
    volumeAmount: '',
    volumeUnit: '',
    productName: '',
    storeName: ''
  });
  
  // Вспомогательные состояния
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null); // Presigned URL для S3 изображений
  const [uploadingImage, setUploadingImage] = useState(false);
  const [recognizingImage, setRecognizingImage] = useState(false);
  const [recognitionResult, setRecognitionResult] = useState(null);
  
  // Справочники для селектов
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingStores, setLoadingStores] = useState(true);
  
  // Типы ценников
  const tagTypes = [
    { id: 'Printed', name: 'Печатный' },
    { id: 'Handwritten', name: 'Рукописный' },
    { id: 'Electronic', name: 'Электронный' }
  ];
  
  // Категории цен
  const priceCategories = [
    { id: 'Unknown', name: 'Неизвестно' },
    { id: 'Minimum', name: 'Минимальная цена' },
    { id: 'Average', name: 'Средняя цена' },
    { id: 'Maximum', name: 'Максимальная цена' }
  ];
  
  // Валюты
  const currencies = [
    { id: 'RUB', name: 'Российский рубль (₽)' },
    { id: 'KZT', name: 'Казахстанский тенге (₸)' }
  ];
  
  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Загружаем справочники
    fetchReferenceData();
    
    // В режиме редактирования загружаем данные ценника
    if (isEditMode) {
      fetchPriceTag();
    }
  }, [id]);
  
  /**
   * Загрузка справочников
   */
  const fetchReferenceData = async () => {
    try {
      // Загрузка товаров
      setLoadingProducts(true);
      const productsData = await productService.getProducts({ pageSize: 100 });
      // Обрабатываем новый формат пагинации API
      const productsArray = Array.isArray(productsData) ? productsData : (productsData.items || productsData.data || []);
      setProducts(productsArray);
      setLoadingProducts(false);
      
      // Загрузка магазинов
      setLoadingStores(true);
      const storesData = await storeService.getStores();
      // Обрабатываем новый формат пагинации API
      const storesArray = Array.isArray(storesData) ? storesData : (storesData.items || storesData.data || []);
      setStores(storesArray);
      setLoadingStores(false);
      
      // Загрузка единиц измерения (для простоты используем моковые данные)
      setUnits([
        { id: 1, name: 'Килограмм', shortName: 'кг' },
        { id: 2, name: 'Грамм', shortName: 'г' },
        { id: 3, name: 'Литр', shortName: 'л' },
        { id: 4, name: 'Миллилитр', shortName: 'мл' },
        { id: 5, name: 'Штука', shortName: 'шт' }
      ]);
    } catch (err) {
      console.error('Ошибка при загрузке справочников:', err);
      toast.error('Не удалось загрузить необходимые данные');
    }
  };
  
  /**
   * Загрузка данных ценника при редактировании
   */
  const fetchPriceTag = async () => {
    try {
      setLoading(true);
      setError(null);
      const priceTag = await priceTagService.getPriceTagById(id);
      
      
      // Получаем вес ценника
      const weightAmount = priceTag.originalWeight || priceTag.overrideWeight || priceTag.weightAmount;
      const weightUnit = priceTag.originalUnit || priceTag.overrideWeightUnit || priceTag.weightUnit || '';

      // Рассчитываем цену за 1 кг если есть цена и вес
      let calculatedPricePerUnit = priceTag.pricePerUnit ? priceTag.pricePerUnit.toString() : '';
      if (!calculatedPricePerUnit && priceTag.price && weightAmount) {
        const price = parseFloat(priceTag.price);
        let weightInGrams = parseFloat(weightAmount);
        // Конвертируем в граммы если кг
        if (weightUnit === 'Kilogram' || weightUnit === 'кг' || weightUnit === 'kg') {
          weightInGrams = weightInGrams * 1000;
        }
        if (weightInGrams > 0) {
          calculatedPricePerUnit = ((price / weightInGrams) * 1000).toFixed(2);
        }
      }

      setFormData({
        productId: priceTag.productId || '',
        storeId: priceTag.storeId || '',
        price: priceTag.price ? priceTag.price.toString() : '',
        pricePerUnit: calculatedPricePerUnit,
        unitId: priceTag.unitId || '',
        discountPrice: priceTag.discountPrice ? priceTag.discountPrice.toString() : '',
        discountStartDate: priceTag.discountStartDate ? new Date(priceTag.discountStartDate).toISOString().split('T')[0] : '',
        discountEndDate: priceTag.discountEndDate ? new Date(priceTag.discountEndDate).toISOString().split('T')[0] : '',
        tagType: priceTag.tagType || 'Printed',
        category: priceTag.category || 'Minimum',
        currency: priceTag.currency || 'RUB',
        imageId: priceTag.imageId || '',
        weightAmount: weightAmount ? weightAmount.toString() : '',
        weightUnit: weightUnit,
        volumeAmount: (priceTag.overrideVolume || priceTag.volumeAmount) ?
          (priceTag.overrideVolume || priceTag.volumeAmount).toString() : '',
        volumeUnit: priceTag.overrideVolumeUnit || priceTag.volumeUnit || '',
        productName: priceTag.product?.name || priceTag.productName || '',
        storeName: priceTag.store?.name || priceTag.storeName || ''
      });
      
      // Если есть изображение, сохраняем его для отображения
      if (priceTag.image) {
        const imageData = {
          id: priceTag.imageId,
          filePath: priceTag.image.filePath,
          fileName: priceTag.image.fileName
        };
        setUploadedImage(imageData);
        
        // Если это S3 ключ, получаем presigned URL
        if (priceTag.image.filePath && priceTag.image.filePath.startsWith('s3://')) {
          try {
            const presignedUrl = await priceTagService.getImagePresignedUrl(priceTag.imageId);
            setUploadedImageUrl(presignedUrl);
          } catch (err) {
            console.error('Ошибка получения presigned URL для изображения:', err);
            // Fallback - используем filePath напрямую (хотя это не сработает для S3)
            setUploadedImageUrl(priceTag.image.filePath);
          }
        } else {
          // Для локальных файлов используем filePath напрямую
          setUploadedImageUrl(priceTag.image.filePath);
        }
      }
    } catch (err) {
      console.error('Ошибка при загрузке ценника:', err);
      setError('Не удалось загрузить данные ценника. Возможно, он был удален или у вас нет доступа.');
      toast.error('Ошибка при загрузке ценника');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Парсинг веса/объема из названия товара
   * @param {string} productName - Название товара
   * @returns {Object} - Объект с количеством и единицей измерения
   */
  const parseWeightVolumeFromName = (productName) => {
    if (!productName) return { amount: null, unit: null, type: null };

    // Регулярные выражения для поиска веса и объема
    const weightPatterns = [
      /(\d+(?:[.,]\d+)?)\s*(г|гр|грамм|gram)/i,
      /(\d+(?:[.,]\d+)?)\s*(кг|килограмм|kg)/i
    ];

    const volumePatterns = [
      /(\d+(?:[.,]\d+)?)\s*(мл|миллилитр|ml)/i,
      /(\d+(?:[.,]\d+)?)\s*(л|литр|liter|litr)/i
    ];

    // Проверяем вес
    for (const pattern of weightPatterns) {
      const match = productName.match(pattern);
      if (match) {
        const amount = parseFloat(match[1].replace(',', '.'));
        const unitStr = match[2].toLowerCase();

        let unit = 'Gram';
        let normalizedAmount = amount;

        if (unitStr.includes('кг') || unitStr === 'kg') {
          unit = 'Kilogram';
        }

        return { amount: normalizedAmount, unit: unit, type: 'weight' };
      }
    }

    // Проверяем объем
    for (const pattern of volumePatterns) {
      const match = productName.match(pattern);
      if (match) {
        const amount = parseFloat(match[1].replace(',', '.'));
        const unitStr = match[2].toLowerCase();

        let unit = 'Milliliter';
        let normalizedAmount = amount;

        if (unitStr.includes('л') || unitStr === 'liter' || unitStr === 'litr') {
          unit = 'Liter';
        }

        return { amount: normalizedAmount, unit: unit, type: 'volume' };
      }
    }

    return { amount: null, unit: null, type: null };
  };

  /**
   * Расчет цены за единицу на основе веса/объема
   */
  const calculatePricePerUnit = (price, productName, weightAmount, weightUnit, volumeAmount, volumeUnit) => {
    if (!price || isNaN(parseFloat(price))) return '';

    const priceFloat = parseFloat(price);
    const parsed = parseWeightVolumeFromName(productName);

    // Если не удалось распарсить из названия, не делаем расчет
    if (!parsed.amount || !parsed.unit) return '';

    // Определяем, что использовать - вес или объем
    let actualAmount = null;
    let actualUnit = null;

    if (parsed.type === 'weight' && weightAmount && weightUnit) {
      actualAmount = parseFloat(weightAmount);
      actualUnit = weightUnit;
    } else if (parsed.type === 'volume' && volumeAmount && volumeUnit) {
      actualAmount = parseFloat(volumeAmount);
      actualUnit = volumeUnit;
    }

    if (!actualAmount || !actualUnit) return '';

    // Приводим к базовым единицам (граммы или миллилитры)
    let amountInBase = actualAmount;
    if (actualUnit === 'Kilogram') {
      amountInBase = actualAmount * 1000;
    } else if (actualUnit === 'Liter') {
      amountInBase = actualAmount * 1000;
    }

    let parsedAmountInBase = parsed.amount;
    if (parsed.unit === 'Kilogram') {
      parsedAmountInBase = parsed.amount * 1000;
    } else if (parsed.unit === 'Liter') {
      parsedAmountInBase = parsed.amount * 1000;
    }

    // Рассчитываем цену за единицу из названия
    const pricePerUnit = (priceFloat / amountInBase) * parsedAmountInBase;

    return pricePerUnit.toFixed(2);
  };

  /**
   * Обработка изменения полей формы
   * @param {Event} e - Событие изменения
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Если изменился товар, берем вес/объем из БД товара
    if (name === 'productId' && value) {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct) {
        const newFormData = {
          ...formData,
          [name]: value,
          productName: selectedProduct.name || ''
        };

        // Берем вес/объем из БД товара (если есть)
        if (selectedProduct.weight && selectedProduct.weightUnit) {
          newFormData.weightAmount = selectedProduct.weight.toString();
          newFormData.weightUnit = selectedProduct.weightUnit;
        } else if (selectedProduct.volume && selectedProduct.volumeUnit) {
          newFormData.volumeAmount = selectedProduct.volume.toString();
          newFormData.volumeUnit = selectedProduct.volumeUnit;
        } else {
          // Fallback: парсим из названия если в БД нет
          const parsed = parseWeightVolumeFromName(selectedProduct.name);
          if (parsed.amount && parsed.unit) {
            if (parsed.type === 'weight') {
              newFormData.weightAmount = parsed.amount.toString();
              newFormData.weightUnit = parsed.unit;
            } else if (parsed.type === 'volume') {
              newFormData.volumeAmount = parsed.amount.toString();
              newFormData.volumeUnit = parsed.unit;
            }
          }
        }

        setFormData(newFormData);
        return;
      }
    }

    const newFormData = {
      ...formData,
      [name]: value
    };

    // Автоматический пересчет цены за единицу при изменении веса/объема или цены
    if (['price', 'weightAmount', 'weightUnit', 'volumeAmount', 'volumeUnit'].includes(name)) {
      const calculatedPrice = calculatePricePerUnit(
        name === 'price' ? value : formData.price,
        formData.productName,
        name === 'weightAmount' ? value : formData.weightAmount,
        name === 'weightUnit' ? value : formData.weightUnit,
        name === 'volumeAmount' ? value : formData.volumeAmount,
        name === 'volumeUnit' ? value : formData.volumeUnit
      );

      if (calculatedPrice) {
        newFormData.pricePerUnit = calculatedPrice;
      }
    }

    setFormData(newFormData);
  };
  
  /**
   * Обработка изменения числовых полей
   * @param {Event} e - Событие изменения
   */
  const handleNumberChange = (e) => {
    const { name, value } = e.target;

    // Допускаем ввод только чисел и точки/запятой
    if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
      // Заменяем запятую на точку для корректной работы с числами
      const normalizedValue = value.replace(',', '.');

      const newFormData = {
        ...formData,
        [name]: normalizedValue
      };

      // Автоматический пересчет цены за единицу при изменении веса/объема или цены
      if (['price', 'weightAmount', 'volumeAmount'].includes(name)) {
        const calculatedPrice = calculatePricePerUnit(
          name === 'price' ? normalizedValue : formData.price,
          formData.productName,
          name === 'weightAmount' ? normalizedValue : formData.weightAmount,
          formData.weightUnit,
          name === 'volumeAmount' ? normalizedValue : formData.volumeAmount,
          formData.volumeUnit
        );

        if (calculatedPrice) {
          newFormData.pricePerUnit = calculatedPrice;
        }
      }

      setFormData(newFormData);
    }
  };
  
  /**
   * Обработчик загрузки изображения
   * @param {Event} e - Событие изменения input[type=file]
   */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setUploadingImage(true);
      setUploadedImageUrl(null); // Сбрасываем старый URL
      const imageData = await priceTagService.uploadImage(file);
      
      
      // Обновляем состояние с загруженным изображением
      const newImageData = {
        id: imageData.id,
        filePath: imageData.filePath,
        fileName: imageData.fileName || file.name
      };
      setUploadedImage(newImageData);
      
      // Если это S3 ключ, получаем presigned URL
      if (imageData.filePath && imageData.filePath.startsWith('s3://')) {
        try {
          const presignedUrl = await priceTagService.getImagePresignedUrl(imageData.id);
          setUploadedImageUrl(presignedUrl);
        } catch (err) {
          console.error('Ошибка получения presigned URL для нового изображения:', err);
          // Fallback - используем filePath напрямую (хотя это не сработает для S3)
          setUploadedImageUrl(imageData.filePath);
        }
      } else {
        // Для локальных файлов используем filePath напрямую
        setUploadedImageUrl(imageData.filePath);
      }
      
      // Обновляем ID изображения в форме, сохраняя существующие поля включая taskId
      setFormData(prevFormData => ({
        ...prevFormData,
        imageId: imageData.id
      }));
      
      toast.success('Изображение успешно загружено');
    } catch (err) {
      console.error('Ошибка при загрузке изображения:', err);
      toast.error('Не удалось загрузить изображение');
    } finally {
      setUploadingImage(false);
    }
  };
  
  /**
   * Парсит строку веса/объема и возвращает значение и единицу
   */
  const parseWeightVolumeString = (str, type) => {
    if (!str || str === 'не найдено') return { amount: null, unit: null };
    
    try {
      // Удаляем лишние пробелы и приводим к нижнему регистру
      const normalized = str.trim().toLowerCase();
      
      // Паттерны для веса
      if (type === 'weight') {
        const weightPatterns = [
          /(\d+(?:[.,]\d+)?)\s*(г|гр|грамм|g|gram)/i,
          /(\d+(?:[.,]\d+)?)\s*(кг|килограмм|kg|kilogram)/i
        ];
        
        for (const pattern of weightPatterns) {
          const match = normalized.match(pattern);
          if (match) {
            const amount = parseFloat(match[1].replace(',', '.'));
            const unitStr = match[2].toLowerCase();
            const unit = (unitStr.includes('кг') || unitStr.includes('kg') || unitStr.includes('kilogram')) ? 'Kilogram' : 'Gram';
            return { amount, unit };
          }
        }
      }
      
      // Паттерны для объема
      if (type === 'volume') {
        const volumePatterns = [
          /(\d+(?:[.,]\d+)?)\s*(мл|миллилитр|ml|milliliter)/i,
          /(\d+(?:[.,]\d+)?)\s*(л|литр|liter|litr)/i
        ];
        
        for (const pattern of volumePatterns) {
          const match = normalized.match(pattern);
          if (match) {
            const amount = parseFloat(match[1].replace(',', '.'));
            const unitStr = match[2].toLowerCase();
            const unit = (unitStr.includes('л') && !unitStr.includes('мл')) || unitStr.includes('liter') || unitStr.includes('litr') ? 'Liter' : 'Milliliter';
            return { amount, unit };
          }
        }
      }
    } catch (err) {
      console.error('Ошибка парсинга веса/объема:', err);
    }
    
    return { amount: null, unit: null };
  };

  /**
   * Обработчик распознавания ценника с помощью AI
   */
  const handleRecognizeImage = async () => {
    if (!formData.imageId) {
      toast.warning('Сначала загрузите изображение');
      return;
    }
    
    try {
      setRecognizingImage(true);
      const result = await priceTagService.recognizePriceTag(formData.imageId);
      setRecognitionResult(result);
      
      // Парсим цену (originalPrice или price для обратной совместимости)
      let priceValue = null;
      if (result.originalPrice && result.originalPrice !== 'не найдено') {
        priceValue = typeof result.originalPrice === 'string' 
          ? parseFloat(result.originalPrice.replace(',', '.')) 
          : result.originalPrice;
      } else if (result.price && result.price !== 'не найдено') {
        priceValue = typeof result.price === 'string' 
          ? parseFloat(result.price.replace(',', '.')) 
          : result.price;
      }
      
      // Парсим цену со скидкой
      let discountPriceValue = null;
      if (result.discountPrice && result.discountPrice !== 'не найдено') {
        discountPriceValue = typeof result.discountPrice === 'string' 
          ? parseFloat(result.discountPrice.replace(',', '.')) 
          : result.discountPrice;
      }
      
      // Парсим вес
      const weightData = parseWeightVolumeString(result.weight, 'weight');
      
      // Парсим объем
      const volumeData = parseWeightVolumeString(result.volume, 'volume');
      
      // Заполняем форму распознанными данными, сохраняя существующие поля включая taskId
      setFormData(prevFormData => ({
        ...prevFormData,
        price: priceValue ? priceValue.toString() : prevFormData.price,
        discountPrice: discountPriceValue ? discountPriceValue.toString() : prevFormData.discountPrice,
        weightAmount: weightData.amount ? weightData.amount.toString() : prevFormData.weightAmount,
        weightUnit: weightData.unit || prevFormData.weightUnit,
        volumeAmount: volumeData.amount ? volumeData.amount.toString() : prevFormData.volumeAmount,
        volumeUnit: volumeData.unit || prevFormData.volumeUnit,
        // Если распознано наименование, можно попробовать найти товар по названию
        // Но это лучше делать отдельной функцией
      }));
      
      toast.success('Ценник успешно распознан');
    } catch (err) {
      console.error('Ошибка при распознавании ценника:', err);
      toast.error('Не удалось распознать ценник');
    } finally {
      setRecognizingImage(false);
    }
  };
  
  /**
   * Обработчик удаления загруженного изображения
   */
  const handleRemoveImage = () => {
    setUploadedImage(null);
    setUploadedImageUrl(null);
    setFormData(prevFormData => ({
      ...prevFormData,
      imageId: ''
    }));
    setRecognitionResult(null);
  };
  
  /**
   * Валидация формы перед отправкой
   * @returns {boolean} - Результат валидации
   */
  const validateForm = () => {
    // Обязательные поля
    if (!formData.productId) {
      toast.error('Выберите товар');
      return false;
    }
    
    if (!formData.storeId) {
      toast.error('Выберите магазин');
      return false;
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Введите корректную цену');
      return false;
    }
    
    // Проверка скидки
    if (formData.discountPrice && parseFloat(formData.discountPrice) >= parseFloat(formData.price)) {
      toast.error('Цена со скидкой должна быть меньше основной цены');
      return false;
    }
    
    // Проверка дат скидки
    if (formData.discountPrice && (formData.discountStartDate || formData.discountEndDate)) {
      if (!formData.discountStartDate) {
        toast.error('Укажите дату начала скидки');
        return false;
      }
      
      if (!formData.discountEndDate) {
        toast.error('Укажите дату окончания скидки');
        return false;
      }
      
      const startDate = new Date(formData.discountStartDate);
      const endDate = new Date(formData.discountEndDate);
      
      if (startDate > endDate) {
        toast.error('Дата начала скидки должна быть раньше даты окончания');
        return false;
      }
    }
    
    return true;
  };
  
  /**
   * Обработчик отправки формы
   * @param {Event} e - Событие отправки формы
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверяем валидность формы
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Подготавливаем данные для отправки
      const priceTagData = {
        productId: formData.productId,
        storeId: parseInt(formData.storeId, 10),
        price: parseFloat(formData.price),
        tagType: formData.tagType,
        category: formData.category,
        currency: formData.currency,
        imageId: formData.imageId || null
      };

      // Добавляем опциональные поля, если они заполнены
      if (formData.pricePerUnit) {
        priceTagData.pricePerUnit = parseFloat(formData.pricePerUnit);
      }

      if (formData.unitId) {
        priceTagData.unitId = parseInt(formData.unitId, 10);
      }

      if (formData.discountPrice) {
        priceTagData.discountPrice = parseFloat(formData.discountPrice);
      }

      if (formData.discountStartDate) {
        priceTagData.discountStartDate = new Date(formData.discountStartDate);
      }

      if (formData.discountEndDate) {
        priceTagData.discountEndDate = new Date(formData.discountEndDate);
      }

      // Добавляем вес/объем (используем OverrideWeight/OverrideVolume для бэкенда)
      if (formData.weightAmount) {
        priceTagData.overrideWeight = parseFloat(formData.weightAmount);
      }

      if (formData.weightUnit) {
        // Конвертируем строковые единицы в enum для бэкенда
        const weightUnitMap = {
          'Gram': 'Gram',
          'Kilogram': 'Kilogram',
          'г': 'Gram',
          'кг': 'Kilogram',
          'g': 'Gram',
          'kg': 'Kilogram'
        };
        priceTagData.overrideWeightUnit = weightUnitMap[formData.weightUnit] || formData.weightUnit;
      }

      if (formData.volumeAmount) {
        priceTagData.overrideVolume = parseFloat(formData.volumeAmount);
      }

      if (formData.volumeUnit) {
        // Конвертируем строковые единицы в enum для бэкенда
        const volumeUnitMap = {
          'Milliliter': 'Milliliter',
          'Liter': 'Liter',
          'мл': 'Milliliter',
          'л': 'Liter',
          'ml': 'Milliliter',
          'l': 'Liter'
        };
        priceTagData.overrideVolumeUnit = volumeUnitMap[formData.volumeUnit] || formData.volumeUnit;
      }

      if (formData.productName && formData.productName.trim() !== '') {
        priceTagData.productName = formData.productName.trim();
      }
      
      // В зависимости от режима вызываем соответствующий метод API
      let result;
      if (isEditMode) {
        // Обновление существующего ценника
        result = await priceTagService.updatePriceTag(id, priceTagData);
        toast.success('Ценник успешно обновлен');
      } else {
        // Создание нового ценника
        result = await priceTagService.createPriceTag(priceTagData);
        toast.success('Ценник успешно создан');
      }
      
      // Перенаправляем на страницу просмотра созданного/обновленного ценника
      navigate(`/price-tags/${result.id}`);
    } catch (err) {
      console.error('Ошибка при сохранении ценника:', err);
      toast.error(isEditMode ? 'Не удалось обновить ценник' : 'Не удалось создать ценник');
    } finally {
      setSubmitting(false);
    }
  };
  
  /**
   * Обработчик отмены и возврата к списку
   */
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/price-tags/${id}`);
    } else {
      navigate('/price-tags');
    }
  };
  
  // Показываем индикатор загрузки при загрузке данных в режиме редактирования
  if (loading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Показываем сообщение об ошибке, если не удалось загрузить данные
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-xl font-bold text-red-700 mb-2">Ошибка</h2>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => navigate('/price-tags')}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
        >
          Вернуться к списку
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={handleCancel}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{pageTitle}</h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Левая колонка - фото ценника */}
            <div className="md:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Изображение ценника</h3>
                
                {uploadedImage ? (
                  <div className="space-y-4">
                    <div className="aspect-square w-full bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={uploadedImageUrl || uploadedImage.filePath} 
                        alt="Загруженный ценник" 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Ошибка загрузки изображения:', uploadedImageUrl || uploadedImage.filePath);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="w-full h-full flex items-center justify-center text-gray-500 text-sm"
                        style={{ display: 'none' }}
                      >
                        Не удалось загрузить изображение
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Удалить изображение
                      </button>
                      <button
                        type="button"
                        onClick={handleRecognizeImage}
                        disabled={recognizingImage}
                        className={`text-blue-600 hover:text-blue-800 text-sm font-medium ${
                          recognizingImage ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {recognizingImage ? 'Распознавание...' : 'Распознать ценник'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="aspect-square w-full bg-gray-100 rounded-lg flex flex-col items-center justify-center p-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-400 text-center mb-4">Загрузите изображение ценника</p>
                      <label className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md cursor-pointer">
                        <span>{uploadingImage ? 'Загрузка...' : 'Выбрать файл'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-gray-500 text-sm text-center">
                      Поддерживаемые форматы: JPG, PNG, WEBP.
                      <br />
                      Максимальный размер файла: 10 МБ.
                    </p>
                  </div>
                )}
                
                {/* Результаты распознавания */}
                {recognitionResult && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-3">Результаты распознавания</h4>
                    <div className="space-y-3">
                      {/* Наименование товара */}
                      {recognitionResult.productName && (
                        <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                          <div className="text-xs text-gray-500 mb-1">Наименование:</div>
                          <div className="text-sm font-medium text-gray-900">{recognitionResult.productName}</div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-2">
                        {/* Обычная цена */}
                        {(recognitionResult.originalPrice || recognitionResult.price) && (
                          <div className="p-2 bg-white rounded">
                            <div className="text-xs text-gray-500 mb-1">Обычная цена:</div>
                            <div className="text-sm font-medium text-gray-900">
                              {typeof recognitionResult.originalPrice === 'string' 
                                ? recognitionResult.originalPrice 
                                : (recognitionResult.originalPrice || recognitionResult.price)} {recognitionResult.currency || '₽'}
                            </div>
                          </div>
                        )}
                        
                        {/* Цена со скидкой */}
                        {recognitionResult.discountPrice && recognitionResult.discountPrice !== 'не найдено' && (
                          <div className="p-2 bg-white rounded border-l-4 border-red-500">
                            <div className="text-xs text-gray-500 mb-1">Цена со скидкой:</div>
                            <div className="text-sm font-medium text-red-600">
                              {typeof recognitionResult.discountPrice === 'string' 
                                ? recognitionResult.discountPrice 
                                : recognitionResult.discountPrice} {recognitionResult.currency || '₽'}
                            </div>
                          </div>
                        )}
                        
                        {/* Вес */}
                        {recognitionResult.weight && recognitionResult.weight !== 'не найдено' && (
                          <div className="p-2 bg-white rounded">
                            <div className="text-xs text-gray-500 mb-1">Вес:</div>
                            <div className="text-sm font-medium text-gray-900">{recognitionResult.weight}</div>
                          </div>
                        )}
                        
                        {/* Объем */}
                        {recognitionResult.volume && recognitionResult.volume !== 'не найдено' && (
                          <div className="p-2 bg-white rounded">
                            <div className="text-xs text-gray-500 mb-1">Объем:</div>
                            <div className="text-sm font-medium text-gray-900">{recognitionResult.volume}</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Точность распознавания */}
                      {recognitionResult.confidence && (
                        <div className="pt-2 border-t border-blue-200">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Точность распознавания:</span>
                            <span className="text-sm font-medium text-gray-900">{(recognitionResult.confidence * 100).toFixed(0)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Правая колонка - данные о ценнике */}
            <div className="md:col-span-2">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-6">Информация о ценнике</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Первая колонка полей */}
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Товар <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="productId"
                        value={formData.productId}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Выберите товар</option>
                        {loadingProducts ? (
                          <option disabled>Загрузка...</option>
                        ) : (
                          products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Магазин <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="storeId"
                        value={formData.storeId}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Выберите магазин</option>
                        {loadingStores ? (
                          <option disabled>Загрузка...</option>
                        ) : (
                          stores.map(store => (
                            <option key={store.id} value={store.id}>
                              {store.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Наименование товара */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Наименование
                      </label>
                      <input
                        type="text"
                        name="productName"
                        value={formData.productName || ''}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Наименование с ценника"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Наименование товара, указанное на ценнике
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Цена <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="price"
                          value={formData.price}
                          onChange={handleNumberChange}
                          placeholder="0.00"
                          className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                          required
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <span className="text-gray-500">₽</span>
                        </div>
                      </div>
                    </div>

                    {/* Вес/Объем */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Вес/Объем
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name={formData.weightUnit || formData.weightAmount ? 'weightAmount' : 'volumeAmount'}
                          value={(formData.weightAmount || formData.volumeAmount) || ''}
                          onChange={handleNumberChange}
                          placeholder="100"
                          className="block flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <select
                          name={formData.weightUnit ? 'weightUnit' : 'volumeUnit'}
                          value={formData.weightUnit || formData.volumeUnit || ''}
                          onChange={(e) => {
                            const { value } = e.target;
                            // Определяем, это вес или объем
                            const isWeight = value === 'Gram' || value === 'Kilogram';
                            const isVolume = value === 'Milliliter' || value === 'Liter';

                            const newFormData = {
                              ...formData
                            };

                            if (isWeight) {
                              newFormData.weightUnit = value;
                              newFormData.volumeUnit = '';
                              newFormData.volumeAmount = '';
                              // Если есть значение в volumeAmount, переносим его в weightAmount
                              if (formData.volumeAmount && !formData.weightAmount) {
                                newFormData.weightAmount = formData.volumeAmount;
                              }
                            } else if (isVolume) {
                              newFormData.volumeUnit = value;
                              newFormData.weightUnit = '';
                              newFormData.weightAmount = '';
                              // Если есть значение в weightAmount, переносим его в volumeAmount
                              if (formData.weightAmount && !formData.volumeAmount) {
                                newFormData.volumeAmount = formData.weightAmount;
                              }
                            } else {
                              // Очистка всех полей если выбрано "Ед."
                              newFormData.weightUnit = '';
                              newFormData.volumeUnit = '';
                            }

                            // Пересчитываем цену за единицу
                            const calculatedPrice = calculatePricePerUnit(
                              formData.price,
                              formData.productName,
                              newFormData.weightAmount,
                              newFormData.weightUnit,
                              newFormData.volumeAmount,
                              newFormData.volumeUnit
                            );

                            if (calculatedPrice) {
                              newFormData.pricePerUnit = calculatedPrice;
                            }

                            setFormData(newFormData);
                          }}
                          className="block w-32 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Ед.</option>
                          <optgroup label="Вес">
                            <option value="Gram">г</option>
                            <option value="Kilogram">кг</option>
                          </optgroup>
                          <optgroup label="Объем">
                            <option value="Milliliter">мл</option>
                            <option value="Liter">л</option>
                          </optgroup>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Вес/объем с ценника. При заполнении автоматически рассчитывается цена за единицу из названия товара.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Цена за единицу
                        {formData.pricePerUnit && (
                          <span className="ml-2 text-xs font-normal text-green-600">
                            (автоматически рассчитано)
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="pricePerUnit"
                          value={formData.pricePerUnit || ''}
                          readOnly
                          placeholder="Будет рассчитано автоматически"
                          className="block w-full p-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed pr-10"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <span className="text-gray-500">₽</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Рассчитывается на основе цены, веса/объема с ценника и единицы измерения из названия товара
                      </p>
                    </div>
                  </div>
                  
                  {/* Вторая колонка полей */}
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Тип ценника
                      </label>
                      <select
                        name="tagType"
                        value={formData.tagType}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {tagTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Категория цены <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {priceCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Выберите, к какой категории относится данная цена
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Валюта
                      </label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {currencies.map(currency => (
                          <option key={currency.id} value={currency.id}>
                            {currency.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Цена со скидкой
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="discountPrice"
                          value={formData.discountPrice}
                          onChange={handleNumberChange}
                          placeholder="0.00"
                          className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <span className="text-gray-500">₽</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Начало скидки
                        </label>
                        <input
                          type="date"
                          name="discountStartDate"
                          value={formData.discountStartDate}
                          onChange={handleChange}
                          className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Конец скидки
                        </label>
                        <input
                          type="date"
                          name="discountEndDate"
                          value={formData.discountEndDate}
                          onChange={handleChange}
                          className="block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Кнопки формы */}
          <div className="flex justify-end mt-6 space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center ${
                submitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Сохранение...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {isEditMode ? 'Сохранить изменения' : 'Создать ценник'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceTagFormPage;